import { useMemo, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Input } from "./ui/input";
import { FACILITY_KINDS, FACILITY_SURFACES, facilityKindLabel, facilitySurfaceLabel } from "../lib/facilities";
import {
  FACILITY_NAME_MAX,
  facilityEditDiff,
  facilityEditErrors,
  facilityEditForm,
  facilityEditPath,
} from "../lib/facilityEdit";
import { ChoiceChips } from "./FacilityAddForm";

/**
 * Issue #601 — rename this field or net, or say what it actually is.
 *
 * THE RULES ARE `lib/facilityEdit.js` — path, diff, validation, gate — and
 * this component is only the surface, on `ConditionUpdate.jsx` and
 * `FacilityAddForm.jsx`'s pattern exactly: it decides NOTHING about who may
 * press it. The caller gates it (`canEditFacility`, which is
 * `canOfferGroundEdit`, which is the server's `checkGroundCorrectionGate`
 * mirrored), so no control is ever shown that the server would 403. A
 * refusal that still arrives — the server owns the "other players rely on
 * this ground" half no client may be told — is SHOWN, not swallowed.
 *
 * A TRUE EDIT IN PLACE, which is the whole point of the ticket. It PATCHes
 * the existing row: the facility keeps its id, and therefore its #211
 * ratings, its `created_at`, and every training session that points at it
 * (`training_sessions.facility_id`). The workaround this replaces — add a
 * new surface, archive the old one — quietly lost all three.
 *
 * CLOSED UNTIL PRESSED, `FacilityAddForm`'s shape: one small button on the
 * row, then the fields inline. A facility row already carries a condition
 * control, a ratings block and an archive toggle; a name field permanently
 * open on each of them would bury the things people came for.
 *
 * ONLY THE CHANGED KEYS TRAVEL (`facilityEditDiff`). Save stays disabled
 * until something is different, which is also the empty patch the API
 * answers 400 to.
 *
 * `onSaved` receives the SERVER'S row (the 200 body), never a local guess —
 * the caller folds it into the same list the condition control and the
 * rating block read from, so the corrected name appears everywhere at once
 * with no reload.
 */
export default function FacilityEditForm({ ground, facility, onSaved, testId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => facilityEditForm(facility));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const path = facilityEditPath(ground?.ground_id, facility?.facility_id);
  const errors = useMemo(() => facilityEditErrors(form), [form]);
  const patch = useMemo(() => facilityEditDiff(facility, form), [facility, form]);
  const nothingChanged = Object.keys(patch).length === 0;
  const invalid = Object.keys(errors).length > 0;

  /** Re-seeded on every open, never on mount alone: between one open and the
   * next this row may have been corrected by somebody else (the list holds
   * the server's rows), and a form still holding the old name would offer to
   * put it back. */
  const openForm = () => {
    setForm(facilityEditForm(facility));
    setError("");
    setOpen(true);
  };

  const closeForm = () => {
    setOpen(false);
    setError("");
  };

  const set = (field) => (value) => {
    setError("");
    setForm((f) => ({ ...f, [field]: value }));
  };

  async function save() {
    if (saving || !path || invalid || nothingChanged) return;
    setSaving(true);
    setError("");
    try {
      const res = await api.patch(path, patch);
      if (onSaved) onSaved(res.data);
      setOpen(false);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={openForm}
        data-testid={`${testId}-open`}
        className="mt-2 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 min-h-11 text-[11px] font-semibold text-slate-600 hover:border-slate-400"
      >
        <Pencil size={11} /> Rename or correct
      </button>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-slate-200 p-2.5" data-testid={testId}>
      <div className="mb-1.5">
        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500" htmlFor={`${testId}-name`}>
          Name
        </label>
        <Input
          id={`${testId}-name`}
          value={form.name}
          maxLength={FACILITY_NAME_MAX}
          disabled={saving}
          onChange={(e) => set("name")(e.target.value)}
          placeholder={'e.g. "Net 1" or "Centre wicket"'}
          className="mt-0.5 h-11"
          data-testid={`${testId}-name`}
        />
        {errors.name && <span className="text-xs text-red-600">{errors.name}</span>}
      </div>
      {/* The closed sets off the shared vocabulary, same chips as the add
          form — a person answering "field or net?" twice in this app should
          be answering it the same way both times. The difference is that
          here one chip is usually already pressed, because the row already
          has an answer; a pre-#544 row has none, and leaving it unanswered
          is allowed (`facilityEditErrors`) so a legacy net can still be
          renamed by somebody who does not know which half it belongs to. */}
      <ChoiceChips
        label="Field or net?"
        choices={FACILITY_KINDS.map((k) => ({ value: k, label: facilityKindLabel(k) }))}
        value={form.kind}
        onChoose={set("kind")}
        disabled={saving}
        testId={`${testId}-kind`}
      />
      <ChoiceChips
        label="Surface"
        choices={FACILITY_SURFACES.map((s) => ({ value: s, label: facilitySurfaceLabel(s) }))}
        value={form.surface}
        onChoose={set("surface")}
        disabled={saving}
        testId={`${testId}-surface`}
      />
      {/* Said out loud because it is the reassurance the ticket is about:
          this corrects the row rather than replacing it, so nothing hanging
          off the surface is lost. */}
      <p className="mt-1 text-[10px] text-slate-500" data-testid={`${testId}-help`}>
        Corrects this surface in place — its ratings and training history stay with it.
      </p>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || invalid || nothingChanged}
          data-testid={`${testId}-save`}
          className="inline-flex items-center gap-1 rounded-md bg-[#0B2E59] px-3 min-h-11 text-[11px] font-semibold text-white disabled:opacity-50"
        >
          {saving && <Loader2 size={11} className="animate-spin" />}
          Save changes
        </button>
        <button
          type="button"
          onClick={closeForm}
          disabled={saving}
          data-testid={`${testId}-cancel`}
          className="rounded-md border border-slate-200 px-3 min-h-11 text-[11px] font-semibold text-slate-600"
        >
          Cancel
        </button>
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
